import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import useApi from '../../utils/useApi';
import BoldSignLogo from '../../images/boldsign_icon.svg';
import HubSpotLogo from '../../images/hubspot_icon.svg';

import styles from './Connect.module.css';

const Connect = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [showHubSpotMessage, setShowHubSpotMessage] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPopup = searchParams.has('popup');
  const api = useApi();

  // Third-party cookie probe state
  const [thirdPartyBlocked, setThirdPartyBlocked] = useState(false);
  const [isProbingTPC, setIsProbingTPC] = useState(true);

  const checkUserStatus = async () => {
    setIsCheckingStatus(true);
    try {
      const response = await api.get('/auth/get-status');
      const data = response.data;
      const isConnected = data === true || data === 'true';
      return isConnected;
    } catch (error) {
      const status = error?.response?.status;
      console.error('Failed to check user status.', { status, error });
      return false;
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleConnectAccounts = () => {
    setIsConnecting(true);
    let connectUrl = `/auth/connect?popup=${isPopup}`;

    const connectionWindow = window.open(
      connectUrl,
      'connectionWindow',
      'width=700,height=600,scrollbars=yes,resizable=yes,toolbar=no,menubar=no'
    );

    const checkClosed = setInterval(async () => {
      if (connectionWindow.closed) {
        clearInterval(checkClosed);
        setIsConnecting(false);
        const isConnected = await checkUserStatus();
        if (isConnected) {
          navigate(isPopup ? '/success?popup=1' : '/success');
        } else {
          // If third-party cookies are blocked, route to error page with specific hint
          if (thirdPartyBlocked) {
            navigate(isPopup ? '/error?popup=1&tpc=blocked' : '/error?tpc=blocked');
          } else {
            navigate(isPopup ? '/error?popup=1' : '/error');
          }
        }
      }
    }, 1000);
  };

  // Perform lightweight third-party cookie test from inside iframe on mount
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        // Minimal probe: set then verify a SameSite=None cookie
        await api.get('/cookie/get');
        const verify = await api.get('/cookie/verify');
        const ok = verify?.data?.ok === true;
        if (!cancelled) {
          const blocked = !ok;
          setThirdPartyBlocked(blocked);
          if (blocked) {
            navigate(isPopup ? '/error?popup=1&tpc=blocked' : '/error?tpc=blocked');
          }
          setIsProbingTPC(false);
        }
      } catch (e) {
        console.error('Third-party cookie probe failed.', e);
        setIsProbingTPC(false);
      }
    };
    probe();
    return () => {
      cancelled = true;
    };
  }, [api, navigate, isPopup]);

  if (isProbingTPC) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.loadingCenter}>
          <div className={`${styles.spinner} ${styles.spinnerLarge}`}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <div className={styles.logoPill}>
          <div className={styles.logoContainer}>
            <span className={styles.logoCircle}>
              <img src={HubSpotLogo} alt="HubSpot Logo" className={`${styles.logo} ${styles.hubspot}`} />
            </span>
            <div className={styles.plus}><span className={`sfIconBase sfAddIcon`}></span></div>
            <span className={styles.logoCircle}>
              <img src={BoldSignLogo} alt="BoldSign Logo" className={`${styles.logo} ${styles.boldsign}`} />
            </span>
          </div>
        </div>

        <h2 className={styles.title}>Connect HubSpot to BoldSign</h2>
        <p className={styles.description}>
          Easily connect your HubSpot and BoldSign accounts to send, track, and manage documents directly from your HubSpot CRM.
        </p>

        <ul className={styles.featureList}>
          <li>Send signature requests from HubSpot</li>
          <li>Auto-fill documents with HubSpot CRM data</li>
          <li>Track signing progress inside HubSpot</li>
          <li>Save completed documents back to your CRM</li>
        </ul>
        {!showHubSpotMessage ? (
            <button
              onClick={handleConnectAccounts}
              className={`${styles.button} ${styles.primary}`}
              disabled={isConnecting || isCheckingStatus}
            >
              {isConnecting ? (
                <>
                  <div className={styles.spinner}></div>
                  Connecting...
                </>
              ) : isCheckingStatus ? (
                <>
                  <div className={styles.spinner}></div>
                  Checking status...
                </>
              ) : (
                'Connect your account'
              )}
            </button>
        ) : (
          <div className={styles.hubspotMessage}>
            <p className={styles.successMessage}>
              Connection process completed! Please check your HubSpot account to verify if the connection was successful.
            </p>
            <button onClick={() => setShowHubSpotMessage(false)} className={styles.button}>
              Try Again
            </button>
          </div>
        )}

        <div className={styles.helpText}>
          Trouble connecting?{' '}
          <a href="mailto:support@boldsign.com" target="_blank" rel="noreferrer">contact support</a> for help.
        </div>
      </div>
    </div>
  );
};

export default Connect;
